// GuestDossierDrawer — the slide-in guest dossier drawer (C3, bead jonathan-6k07).
//
// Faithful port of the dossier drawer in Jonathan.dc.html (Face B made visible):
//   header (initials/name/for-line), 'What they came for' (colorGoal),
//   "In {name}'s own words — kept exactly" (verbatim), 'What Jonathan quietly read'
//   (inferred chips w/ source + qualitative dot), 'Mapped to your menu'
//   (service + Booked + control line), 'Confirm in the chair' (stylist_flags),
//   'Heads up' (sensitivity), and a "See {name}'s side" CTA -> C5 widget replay.
//
// Wired to GET /v1/owner/conversations/:id/dossier (jonathan-hg54), which returns
// { conversation_id, dossier: render_stylist_prep(...), generated_at }. The
// dossier dict shape (jonathan/conversation/consult_faces.py::render_stylist_prep):
//   {
//     client_name, intent,
//     client_words:  { history?, requested? },           // their verbatim words
//     reasoning:     { <slot>: { value, certainty, source } },  // inferred, missing dropped
//     service_map:   [ { client_goal, service, certainty, reason, fold_into_appointment } ],
//     selfie_refs:   [ { s3_key, read_quality, rationale, url? } ],  // presigned GET url
//     stylist_flags: [ str ],                            // confirm-in-chair
//     sensitivities: [ str ],
//   }
//
// The endpoint is built-as-code but not deployed yet (deploy bead h07w), so this
// degrades gracefully: loading / empty / error states, never crashes on a 404.
//
// LOCKED DECISIONS honored: NO numeric confidence anywhere (qualitative dot only:
// covered=olive, partial=ochre, missing dropped upstream). Plain language. No
// quick-reply chips. stylist_flags are surfaced as confirm-in-chair prompts,
// never client gates. Clean single-path component — no dual-path, no v2 file.

import { useEffect, useState, useCallback } from 'preact/hooks';
import { apiFetch, ApiError } from '../api/client';
import { ASSISTANT_NAME } from '../brand.js';

// Tone gradients aren't backend-provided; derive a stable one per guest name so
// the drawer avatar matches the inbox card (GuestInboxView uses the same set).
const TONE_GRADIENTS = [
  'linear-gradient(140deg,#8D9FF1,#5566c9)', // periwinkle
  'linear-gradient(140deg,#C5512F,#8f3a20)', // terracotta
  'linear-gradient(140deg,#56683F,#33401E)', // olive
  'linear-gradient(140deg,#9c8468,#6f5c46)', // taupe
  'linear-gradient(140deg,#C79A3E,#94701f)', // ochre
];

function toneFor(name) {
  const key = name || '';
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return TONE_GRADIENTS[Math.abs(hash) % TONE_GRADIENTS.length];
}

function initialsFor(name) {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function firstNameOf(name) {
  if (!name) return 'this guest';
  return name.trim().split(/\s+/)[0];
}

// Qualitative certainty -> dot color. NEVER a numeric score (locked decision).
// covered reads as confident olive; partial reads as the warmer ochre; anything
// else falls back to ochre so a chip is never colorless.
function dotColor(certainty) {
  return certainty === 'covered' ? '#56683F' : '#C79A3E';
}

// Humanize an inferred reasoning slot into a plain-language chip label. The
// backend keeps slot keys (change_intent, color_history, ...) + a value; the
// drawer shows the value when present, else a readable slot name.
const SLOT_LABELS = {
  change_intent: 'Change intent',
  color_history: 'Color history',
  maintenance_pref: 'Maintenance',
  gray_coverage: 'Gray coverage',
  budget: 'Budget',
  deadline: 'Timeline',
};

function chipLabel(slot, info) {
  if (info && info.value != null && String(info.value).trim() !== '') {
    return String(info.value);
  }
  return SLOT_LABELS[slot] || slot;
}

// Source line on a chip ("their words" / "selfie"), mirroring the .dc.html.
function chipSource(info) {
  const src = info && info.source;
  if (!src) return '';
  if (src === 'selfie' || src === 'vision') return 'selfie';
  if (src === 'stated' || src === 'words' || src === 'client') return 'their words';
  return src;
}

export function GuestDossierDrawer({ guestId, guestName, onClose, onSeeHerSide }) {
  const [state, setState] = useState('loading'); // loading | ready | empty | error
  const [dossier, setDossier] = useState(null);

  const load = useCallback(async () => {
    if (!guestId) return;
    setState('loading');
    try {
      const data = await apiFetch(
        `/v1/owner/conversations/${encodeURIComponent(guestId)}/dossier`,
      );
      const d = data && data.dossier;
      if (!d) {
        setState('empty');
        setDossier(null);
        return;
      }
      setDossier(d);
      setState('ready');
    } catch (err) {
      // 404 = not deployed yet OR no dossier for this thread; both degrade to a
      // calm empty/error panel rather than crashing the drawer.
      if (err instanceof ApiError && err.status === 404) {
        setState('empty');
      } else {
        setState('error');
      }
      setDossier(null);
    }
  }, [guestId]);

  useEffect(() => {
    load();
  }, [load]);

  // Lock body scroll while the drawer is open, restore on close.
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Escape closes the drawer (matches the .dc.html backdrop click-to-close).
  useEffect(() => {
    function handleEscape(e) {
      if (e.key === 'Escape') onClose?.();
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // --- derive display values from render_stylist_prep -------------------------
  const name = (dossier && dossier.client_name) || guestName || 'Guest';
  const first = firstNameOf(name);
  const tone = toneFor(name);

  const words =
    dossier &&
    dossier.client_words &&
    (dossier.client_words.history || dossier.client_words.requested);

  // 'What they came for' — the leading goal. Prefer the intent line; fall back to
  // their requested words so the headline never reads empty when intent is sparse.
  const colorGoal =
    (dossier && dossier.intent) ||
    (dossier && dossier.client_words && dossier.client_words.requested) ||
    '';

  const reasoning = (dossier && dossier.reasoning) || {};
  const inferredChips = Object.keys(reasoning).map((slot) => ({
    slot,
    label: chipLabel(slot, reasoning[slot]),
    src: chipSource(reasoning[slot]),
    dot: dotColor(reasoning[slot] && reasoning[slot].certainty),
  }));

  const serviceMap = (dossier && dossier.service_map) || [];
  const primary = serviceMap[0] || null;
  // Control line: if the booked service folds the rest into the appointment, say
  // so plainly — that IS the "nothing risky locked, you confirm in chair" promise.
  const controlLine = primary
    ? primary.fold_into_appointment
      ? `${ASSISTANT_NAME} booked the base service only. You confirm the rest in the chair — nothing risky is locked.`
      : 'Booked to your menu. The details are yours to confirm in the chair.'
    : '';

  const flags = (dossier && dossier.stylist_flags) || [];
  const sensitivities = (dossier && dossier.sensitivities) || [];
  const sensitivityLine = sensitivities.length
    ? sensitivities.join(' · ')
    : 'None mentioned — confirm allergies at check-in.';

  const selfies = (dossier && dossier.selfie_refs) || [];
  const readableSelfie = selfies.find((s) => s && s.url);

  return (
    <>
      {/* Backdrop — click to close (matches the .dc.html scrim). */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(34,30,23,.42)',
          zIndex: 40,
        }}
      />

      {/* Drawer — slide-in from the right, 545px wide (matches the .dc.html). */}
      <aside
        class="dossier-drawer"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '545px',
          maxWidth: '96vw',
          background: '#F4F0E7',
          zIndex: 50,
          boxShadow: '-24px 0 60px rgba(34,30,23,.2)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* sticky drawer header */}
          <div
            style={{
              padding: '26px 30px 20px',
              borderBottom: '1px solid rgba(34,30,23,.08)',
              background: '#F4F0E7',
              position: 'sticky',
              top: 0,
              zIndex: 2,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '18px',
              }}
            >
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '.14em',
                  textTransform: 'uppercase',
                  color: 'var(--olive)',
                }}
              >
                Guest dossier
              </span>
              <span
                onClick={onClose}
                style={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: 'var(--ink-soft)',
                  cursor: 'pointer',
                  padding: '6px 12px',
                  borderRadius: '100px',
                  background: '#EAE4D7',
                }}
              >
                Close ✕
              </span>
            </div>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <div
                style={{
                  flex: 'none',
                  width: '56px',
                  height: '56px',
                  borderRadius: '16px',
                  background: tone,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FBF8F1',
                  fontWeight: 700,
                  fontSize: '18px',
                }}
              >
                {initialsFor(name)}
              </div>
              <div>
                <div
                  class="font-serif"
                  style={{
                    fontWeight: 600,
                    fontSize: '26px',
                    color: 'var(--ink)',
                    marginBottom: '3px',
                  }}
                >
                  {name}
                </div>
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'var(--ink-soft)',
                  }}
                >
                  Guest dossier · Face B
                </div>
              </div>
            </div>
          </div>

          {/* body */}
          <div style={{ padding: '24px 30px 36px' }}>
            {state === 'loading' && <DrawerStatus>Reading {first}'s dossier…</DrawerStatus>}

            {state === 'error' && (
              <DrawerStatus tone="alert">
                <div style={{ marginBottom: '10px' }}>
                  Couldn't load this dossier right now.
                </div>
                <button onClick={load} class="dossier-retry">
                  Try again
                </button>
              </DrawerStatus>
            )}

            {state === 'empty' && (
              <DrawerStatus>
                <div
                  class="font-serif"
                  style={{ fontStyle: 'italic', fontSize: '18px', color: '#3A352C', marginBottom: '6px' }}
                >
                  Nothing read yet.
                </div>
                <div>
                  Once {first} chats with {ASSISTANT_NAME}, their dossier fills in here.
                </div>
              </DrawerStatus>
            )}

            {state === 'ready' && (
              <>
                {/* their goal — leads with what they came for */}
                <div
                  style={{
                    background: 'linear-gradient(140deg,#EEF0E6,#F4F0E7)',
                    border: '1px solid rgba(86,104,63,.2)',
                    borderRadius: '18px',
                    padding: '22px 24px',
                    marginBottom: '22px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '11.5px',
                      fontWeight: 700,
                      letterSpacing: '.13em',
                      textTransform: 'uppercase',
                      color: 'var(--olive)',
                      marginBottom: '10px',
                    }}
                  >
                    What they came for
                  </div>
                  <div
                    class="font-serif"
                    style={{
                      fontWeight: 500,
                      fontSize: '25px',
                      lineHeight: 1.25,
                      color: 'var(--ink)',
                      letterSpacing: '-.01em',
                    }}
                  >
                    {colorGoal || 'Still gathering what they want.'}
                  </div>
                </div>

                {/* their words verbatim */}
                {words && (
                  <div style={{ marginBottom: '24px' }}>
                    <SectionLabel>In {first}'s own words — kept exactly</SectionLabel>
                    <div style={{ borderLeft: '3px solid #8D9FF1', padding: '2px 0 2px 18px' }}>
                      <div
                        class="font-serif"
                        style={{
                          fontStyle: 'italic',
                          fontSize: '17.5px',
                          lineHeight: 1.55,
                          color: '#3A352C',
                        }}
                      >
                        {'“'}
                        {words}
                        {'”'}
                      </div>
                    </div>
                  </div>
                )}

                {/* what jonathan quietly read — inferred chips, qualitative dot */}
                {inferredChips.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <SectionLabel>What {ASSISTANT_NAME} quietly read</SectionLabel>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {inferredChips.map((chip) => (
                        <div
                          key={chip.slot}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: 'var(--surface)',
                            border: '1px solid rgba(34,30,23,.08)',
                            borderRadius: '100px',
                            padding: '7px 14px 7px 11px',
                          }}
                        >
                          <span
                            style={{
                              flex: 'none',
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: chip.dot,
                            }}
                          />
                          <span
                            style={{ fontSize: '13px', fontWeight: 600, color: '#3A352C' }}
                          >
                            {chip.label}
                          </span>
                          {chip.src && (
                            <span
                              style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: 'var(--ink-faint)',
                              }}
                            >
                              {chip.src}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* selfie — presigned GET when readable (jonathan-xhic) */}
                {readableSelfie && (
                  <div style={{ marginBottom: '24px' }}>
                    <SectionLabel>What they sent</SectionLabel>
                    <img
                      src={readableSelfie.url}
                      alt={`${first}'s selfie`}
                      style={{
                        width: '150px',
                        height: '188px',
                        objectFit: 'cover',
                        borderRadius: '18px',
                        boxShadow: '0 6px 16px rgba(34,30,23,.18)',
                      }}
                    />
                    {readableSelfie.rationale && (
                      <div
                        style={{
                          fontSize: '12.5px',
                          fontWeight: 600,
                          color: 'var(--ink-soft)',
                          marginTop: '8px',
                          maxWidth: '320px',
                          lineHeight: 1.4,
                        }}
                      >
                        {readableSelfie.rationale}
                      </div>
                    )}
                  </div>
                )}

                {/* mapped to your menu — service + Booked + control line */}
                {primary && (
                  <div
                    style={{
                      background: 'var(--surface)',
                      border: '1px solid rgba(34,30,23,.09)',
                      borderRadius: '18px',
                      padding: '20px 22px',
                      marginBottom: '14px',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '11.5px',
                        fontWeight: 700,
                        letterSpacing: '.13em',
                        textTransform: 'uppercase',
                        color: 'var(--olive)',
                        marginBottom: '12px',
                      }}
                    >
                      Mapped to your menu
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: controlLine ? '14px' : 0,
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: '19px',
                            fontWeight: 800,
                            color: 'var(--ink)',
                            marginBottom: '3px',
                          }}
                        >
                          {primary.service || 'Service to confirm'}
                        </div>
                        {primary.reason && (
                          <div
                            style={{
                              fontSize: '13.5px',
                              fontWeight: 600,
                              color: 'var(--ink-muted)',
                              lineHeight: 1.4,
                            }}
                          >
                            {primary.reason}
                          </div>
                        )}
                      </div>
                      <span
                        style={{
                          flex: 'none',
                          fontSize: '12.5px',
                          fontWeight: 800,
                          color: '#3C4730',
                          background: 'var(--olive-chip)',
                          padding: '7px 13px',
                          borderRadius: '100px',
                        }}
                      >
                        Booked
                      </span>
                    </div>
                    {controlLine && (
                      <div
                        style={{
                          display: 'flex',
                          gap: '10px',
                          alignItems: 'flex-start',
                          background: 'var(--olive-tint)',
                          borderRadius: '12px',
                          padding: '12px 14px',
                        }}
                      >
                        <span
                          style={{
                            flex: 'none',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            background: 'var(--olive)',
                            color: '#F3EFE6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '11px',
                            fontWeight: 800,
                            marginTop: '1px',
                          }}
                        >
                          ✓
                        </span>
                        <span
                          style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#3C4730',
                            lineHeight: 1.45,
                          }}
                        >
                          {controlLine}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* confirm in the chair — stylist_flags (never a client gate) */}
                {flags.length > 0 && (
                  <div
                    style={{
                      background: 'var(--surface)',
                      border: '1px solid rgba(34,30,23,.09)',
                      borderRadius: '18px',
                      padding: '20px 22px',
                      marginBottom: '14px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '9px',
                        marginBottom: '14px',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '11.5px',
                          fontWeight: 700,
                          letterSpacing: '.13em',
                          textTransform: 'uppercase',
                          color: 'var(--terracotta)',
                        }}
                      >
                        Confirm in the chair
                      </span>
                      <span
                        style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink-faint)' }}
                      >
                        — your call, not the bot's
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {flags.map((f, i) => (
                        <div
                          key={i}
                          style={{ display: 'flex', gap: '11px', alignItems: 'flex-start' }}
                        >
                          <span
                            style={{
                              flex: 'none',
                              width: '7px',
                              height: '7px',
                              borderRadius: '50%',
                              background: 'var(--terracotta)',
                              marginTop: '7px',
                            }}
                          />
                          <span
                            style={{
                              fontSize: '14px',
                              fontWeight: 500,
                              color: '#3A352C',
                              lineHeight: 1.45,
                            }}
                          >
                            {f}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* heads up — sensitivity */}
                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    padding: '14px 18px',
                    background: '#F6EFE6',
                    border: '1px solid rgba(197,81,47,.16)',
                    borderRadius: '14px',
                    marginBottom: '26px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '11.5px',
                      fontWeight: 700,
                      letterSpacing: '.06em',
                      textTransform: 'uppercase',
                      color: 'var(--terracotta)',
                      flex: 'none',
                    }}
                  >
                    Heads up
                  </span>
                  <span
                    style={{
                      fontSize: '13.5px',
                      fontWeight: 600,
                      color: '#5A4A40',
                      lineHeight: 1.4,
                    }}
                  >
                    {sensitivityLine}
                  </span>
                </div>

                {/* see their side — CTA to the widget replay (C5) */}
                <div
                  onClick={() => onSeeHerSide?.(guestId, name)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '14px',
                    background: 'var(--ink)',
                    borderRadius: '16px',
                    padding: '18px 22px',
                    cursor: 'pointer',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: '15px',
                        fontWeight: 700,
                        color: '#F3EFE6',
                        marginBottom: '3px',
                      }}
                    >
                      View recent conversation
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#A9B58C' }}>
                      Recent saved messages with {ASSISTANT_NAME}
                    </div>
                  </div>
                  <span style={{ flex: 'none', fontSize: '20px', color: '#F3EFE6' }}>→</span>
                </div>
              </>
            )}
          </div>
        </div>
      </aside>

      <style>{`
        .dossier-drawer {
          animation: dossierSlideIn 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
        }
        @keyframes dossierSlideIn {
          from { transform: translateX(106%); }
          to { transform: translateX(0); }
        }
        .dossier-retry {
          font-size: 13px;
          font-weight: 700;
          color: var(--olive);
          background: var(--olive-tint);
          border: 1px solid rgba(86,104,63,.22);
          padding: 8px 16px;
          border-radius: 100px;
          cursor: pointer;
        }
        @media (max-width: 640px) {
          .dossier-drawer {
            width: 100% !important;
            max-width: 100% !important;
            animation-name: dossierSlideUp;
          }
          @keyframes dossierSlideUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
        }
      `}</style>
    </>
  );
}

function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontSize: '11.5px',
        fontWeight: 700,
        letterSpacing: '.13em',
        textTransform: 'uppercase',
        color: 'var(--ink-soft)',
        marginBottom: '11px',
      }}
    >
      {children}
    </div>
  );
}

function DrawerStatus({ children, tone }) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: '48px 24px',
        color: tone === 'alert' ? 'var(--terracotta)' : 'var(--ink-soft)',
        fontSize: '14px',
        fontWeight: 600,
      }}
    >
      {children}
    </div>
  );
}
